import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@tapresearch/ui/components';
import { TapAnswer } from '../../../../api';

interface ResultsTableViewProps {
  tableData: Record<string, unknown>[];
  tapAnswers: TapAnswer[];
  usePercentage: boolean;
  crossTabLabels?: Record<string, string>;
}
export const ResultsTableView = ({
  tableData,
  tapAnswers,
  crossTabLabels,
  usePercentage,
}: ResultsTableViewProps) => {
  const orderedData = [...tableData].reverse();
  return (
    <Box
      data-testid="result-table-view"
      flexGrow={1}
      my={6}
      overflowX="scroll"
      overflowY="scroll"
      maxH="500px"
    >
      <Table
        variant="simple"
        fontSize="sm"
        sx={{
          borderCollapse: 'separate',
          borderSpacing: '0',
        }}
      >
        <Thead>
          <Tr
            data-testid={
              !!crossTabLabels
                ? 'cross-tab-table-thead'
                : 'standard-table-thead'
            }
            sx={{
              th: {
                textTransform: 'none',
                color: 'gray.900',
                fontWeight: 'normal',
                fontSize: 'sm',
                letterSpacing: 0,
                position: 'sticky',
                top: 0,
                bg: 'white',
              },
            }}
          >
            {!!crossTabLabels && (
              <CrossTabTHead
                crossTabLabels={crossTabLabels}
                orderedData={orderedData}
              />
            )}
          </Tr>
        </Thead>
        <Tbody>
          {!crossTabLabels ? (
            orderedData.map((answer, i) => {
              const answerId = answer.answer_id as string;
              const [answerPct, answerTotal] = [
                answer[answerId],
                answer[`${answerId}_total`],
              ];
              const answerLabel = tapAnswers.find(
                (ta) => ta.attributes.id.toString() === answerId
              )?.attributes?.option_text;

              return (
                <Tr data-testid="standard-table-row" key={i}>
                  <Td w="50%" color="gray.700">
                    {answerLabel}
                  </Td>
                  <Td color="gray.900" letterSpacing="tight">
                    <Flex align="center">
                      <Text fontWeight="semibold">{answerTotal || 0}</Text>
                      <Text fontSize="sm" pl={2}>
                        ({Number(answerPct || 0).toFixed(2)}%)
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              );
            })
          ) : (
            <>
              {[...tapAnswers].reverse().map((ta) => (
                <CrossTabRow
                  key={ta.id}
                  tapAnswer={ta}
                  orderedData={orderedData}
                  crossTabLabels={crossTabLabels}
                  usePercentage={usePercentage}
                />
              ))}
            </>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

const CrossTabRow = ({
  tapAnswer,
  crossTabLabels,
  orderedData,
  usePercentage,
}: {
  tapAnswer: TapAnswer;
  crossTabLabels: Record<string, string>;
  orderedData: Record<string, unknown>[];
  usePercentage: boolean;
}) => {
  const { id: answerId, option_text: answerLabel } = tapAnswer.attributes;

  return (
    <Tr data-testid="cross-tab-table-row" key={answerId}>
      <Td maxW="50%" color="gray.700">
        {answerLabel}
      </Td>
      {Object.values(crossTabLabels).map((labelText, i) => {
        const crossTabValues = orderedData.find(
          ({ answer_id }) => answer_id === labelText
        );
        if (!crossTabValues) return null;

        const answerValue = usePercentage
          ? crossTabValues[`${answerId}`] || 0
          : crossTabValues[`${answerId}_total`] || 0;

        return (
          <Td
            key={`crossTab-${i}`}
            letterSpacing="tight"
            color={!answerValue ? 'gray.300' : 'gray.900'}
            fontWeight="semibold"
            textAlign="center"
          >
            {usePercentage ? `${Number(answerValue).toFixed(2)}%` : answerValue}
          </Td>
        );
      })}
    </Tr>
  );
};

const CrossTabTHead = ({
  crossTabLabels,
  orderedData,
}: {
  crossTabLabels: Record<string, string>;
  orderedData: Record<string, unknown>[];
}) => (
  <>
    <Th />
    {Object.values(crossTabLabels).map((labelText, i) => {
      const crossTabValues = orderedData.find(
        ({ answer_id }) => answer_id === labelText
      );

      return (
        crossTabValues && (
          <Th key={i} textAlign="center" color="gray.700">
            {labelText}
          </Th>
        )
      );
    })}
  </>
);
